<template>
	<v-layout class="top-bar">
		<v-toolbar-title class="d-flex align-center">
			<v-slide-group show-arrows>
				<v-slide-group-item v-for="user in sessionStore.users" :key="user.userId">
					<v-chip class="user-chips">
						<Flag :lang="user.lang" />
						{{ user.name }}
					</v-chip>
				</v-slide-group-item>
			</v-slide-group>
		</v-toolbar-title>

		<!-- Alternis "Fake" Call Workaround -->
		<v-btn
			v-if="debugStore.altKeyHeld && domainStore.hasAccessRights(UserRole.Editor)"
			variant="text"
			icon="mdi-phone"
			:loading="isCreating"
			:disabled="instanceWorkspacesStore.isOpenedWorkspaceLocked"
			@click.stop="createSessionWithRtp"
		/>

		<!-- DEBUG BUTTON-->
		<v-btn
			v-if="showDebugBtn"
			:variant="debugStore.debugMode ? 'outlined' : 'text'"
			icon="mdi-bug"
			:color="debugStore.debugMode ? 'red' : ''"
			@click.stop="toggleDebugMode"
		></v-btn>

		<!-- OPEN CHAT IN ALTERNIS BUTTON -->
		<v-btn
			v-if="debugStore.altKeyHeld && domainStore.hasAccessRights(UserRole.Editor)"
			variant="text"
			icon="mdi-share"
			@click.stop="openAlternisTab"
		></v-btn>

		<!-- KI BOT BUTTON -->
		<v-menu v-if="domainStore.isEditorOrAdmin" @update:model-value="onMenuUpdate" location="end">
			<template v-slot:activator="{ props }">
				<v-btn
					variant="text"
					icon="mdi-robot"
					v-bind="props"
					:disabled="availableBots.length < 1 || instanceWorkspacesStore.isOpenedWorkspaceLocked"
				></v-btn>
			</template>
			<v-card min-width="250" elevation="3">
				<v-list width="250">
					<div
						v-for="bot in availableBots.sort((a, b) => a.botName.localeCompare(b.botName))"
						:key="bot.botId"
					>
						<v-tooltip :disabled="bot.botName.length < 25" :text="bot.botName" open-delay="500">
							<template v-slot:activator="{ props }">
								<v-list-item
									v-bind="bot.botName.length >= 25 ? props : {}"
									:style="{
										background: activeBots.find((b) => b.botId == bot.botId)
											? 'rgb(var(--v-theme-primary))'
											: undefined,
										color: activeBots.find((b) => b.botId == bot.botId) ? 'black' : undefined,
									}"
									@click="() => toggleBot(bot.botId)"
								>
									<v-list-item-title>{{ bot.botName }}</v-list-item-title>
								</v-list-item>
							</template>
						</v-tooltip>
					</div>
				</v-list>
			</v-card>
		</v-menu>

		<!-- SETTINGS BUTTON -->
		<v-menu v-if="domainStore.isEditorOrAdmin" :close-on-content-click="false" location="end">
			<template v-slot:activator="{ props }">
				<v-btn variant="text" icon="mdi-cog" v-bind="props"></v-btn>
			</template>
			<v-card min-width="250" elevation="3">
				<v-switch
					v-model="configStore.playSynth"
					color="primary"
					:label="symbolsStore.GUILabel('46b18c49-78e4-4456-a908-2a1cb56748e9')"
					hide-details
				></v-switch>
				<v-switch
					v-model="configStore.playTranslation"
					color="primary"
					:label="symbolsStore.GUILabel('28f85dc4-dc07-44ed-9622-11d0ffac089b')"
					hide-details
				></v-switch>
				<v-switch
					v-model="configStore.playTranslationPlayback"
					color="primary"
					:label="symbolsStore.GUILabel('95783876-00fc-4c67-bcc6-ed3f384a1aa4')"
					hide-details
				></v-switch>
				<v-switch
					v-model="configStore.holdToRecord"
					color="primary"
					:label="symbolsStore.GUILabel('bd38b604-3d56-4908-9049-2fed663ba054')"
					hide-details
				></v-switch>
				<v-tooltip v-if="domainStore.isAdmin" location="start">
					<template v-slot:activator="{ props }">
						<div v-bind="props">
							<v-select
								v-model="displayedSuggestionModel"
								:label="symbolsStore.GUILabel('45fc6ce1-101f-47ba-a8ac-de8c593f026b')"
								:items="configStore.suggestionModels"
								hide-details
								prepend-icon="mdi-cube-outline"
								class="mx-2"
								clearable
								@click:clear="
									configStore.resetUserSuggestionModel(
										domainStore.domain.id,
										dataspaceStore.openedDataspaceId || '',
									)
								"
							>
								<template v-slot:selection="{ item }">
									<v-icon
										v-if="
											item.value ===
											configStore.userSuggestionModels[domainStore.domain.id]?.[
												dataspaceStore.openedDataspaceId || ''
											]
										"
										size="small"
										class="mr-1"
									>
										mdi-account
									</v-icon>
									<v-icon
										v-else-if="item.value === configStore.dataspaceSuggestionModel"
										size="small"
										class="mr-1"
									>
										mdi-database
									</v-icon>
									<v-icon
										v-else-if="item.value === configStore.defaultSuggestionModel"
										size="small"
										class="mr-1"
									>
										mdi-earth
									</v-icon>
									{{ item.value }}
								</template>
								<template v-slot:item="{ item, props: itemProps }">
									<v-list-item v-bind="itemProps">
										<template v-slot:title>
											<v-icon
												v-if="
													item.value ===
													configStore.userSuggestionModels[domainStore.domain.id]?.[
														dataspaceStore.openedDataspaceId || ''
													]
												"
												size="small"
												class="mr-1"
											>
												mdi-account
											</v-icon>
											<v-icon
												v-else-if="item.value === configStore.dataspaceSuggestionModel"
												size="small"
												class="mr-1"
											>
												mdi-database
											</v-icon>
											<v-icon
												v-else-if="item.value === configStore.defaultSuggestionModel"
												size="small"
												class="mr-1"
											>
												mdi-earth
											</v-icon>
											{{ item.value }}
										</template>
									</v-list-item>
								</template>
							</v-select>
						</div>
					</template>
					<span>{{
						configStore.getSuggestionModelSourceText(
							domainStore.domain.id,
							dataspaceStore.openedDataspaceId || "",
						)
					}}</span>
				</v-tooltip>
				<v-switch
					v-model="configStore.wordHighlighting"
					color="primary"
					:label="symbolsStore.GUILabel('ffb90193-7bd5-4dc4-96b7-68a348c37da7')"
					hide-details
				></v-switch>
			</v-card>
		</v-menu>
	</v-layout>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from "vue";
import { api } from "@lr-notitia/notitia-alternis/client";
import { useSessionStore } from "../../../../store/alternis/session";
import { useConfigStore, type SuggestionModel } from "../../../../store/alternis/config";
import { useDomainStore } from "../../../../store/domain";
import { useInstanceDataspacesStore } from "../../../../store/i-dataspace";
import { useDebugStore } from "../../../../store/debug";
import { useSymbolsStore } from "../../../../store/symbols";
import { useInstanceWorkspacesStore } from "../../../../store/i-workspace";
import { useAlternisNewStore } from "../../../../store/alternis_new";
import { UserRole } from "@lr-notitia/notitia-operator/schemas";

import Flag from "./Flag.vue";

const props = defineProps<{
	sessionId: string;
}>();

const sessionStore = useSessionStore();
const configStore = useConfigStore();
const domainStore = useDomainStore();
const dataspaceStore = useInstanceDataspacesStore();
const symbolsStore = useSymbolsStore();
const debugStore = useDebugStore();
const instanceWorkspacesStore = useInstanceWorkspacesStore();
const alternisNewStore = useAlternisNewStore();

const showDebugBtn = computed(() => debugStore.debugMode || (domainStore.isEditorOrAdmin && debugStore.altKeyHeld));
const isCreating = ref(false);

const displayedSuggestionModel = computed({
	get: () => configStore.getSuggestionModel(domainStore.domain.id, dataspaceStore.openedDataspaceId || ""),
	set: (val: SuggestionModel) => {
		configStore.setUserSuggestionModel(domainStore.domain.id, dataspaceStore.openedDataspaceId || "", val);
	},
});

window.addEventListener("keydown", (e) => {
	if (e.altKey) debugStore.setAltKeyHeld(true);
});
window.addEventListener("keyup", (e) => {
	if (!e.altKey) debugStore.setAltKeyHeld(false);
});

const availableBots = ref<{ botId: string; botName: string }[]>([]);
const activeBots = ref<{ botId: string; botName: string }[]>([]);

function loadBots() {
	api()
		.bots()
		.get(domainStore.account.id, domainStore.domain.id)
		.then((bots) => {
			availableBots.value = bots;
		});
}

function loadSessionBots() {
	if (!sessionStore.sessionId) return;
	api()
		.session()
		.bots()
		.get(sessionStore.sessionId)
		.then((bots) => {
			activeBots.value = bots;
		});
}

function toggleBot(botId: string) {
	if (!sessionStore.sessionId) return;
	if (instanceWorkspacesStore.isOpenedWorkspaceLocked) return;
	if (
		activeBots.value.find((bot) => {
			return bot.botId === botId;
		})
	) {
		api().session().bots().del(sessionStore.sessionId, botId);
		// timeout just so that the menu can close before the bot is removed, so that the removal of the background fill does not happen immediately (looks weird)
		setTimeout(() => (activeBots.value = activeBots.value.filter((bot) => bot.botId !== botId)), 200);
	} else {
		api().session().bots().post(sessionStore.sessionId, botId);
		// timeout just so that the menu can close before the bot is added, so that background fill does not happen immediately (looks weird)
		setTimeout(
			() =>
				activeBots.value.push({
					botId,
					botName: availableBots.value.find((bot) => bot.botId === botId)?.botName ?? "",
				}),
			200,
		);
	}
}

function onMenuUpdate(isOpen: boolean) {
	if (isOpen) {
		loadSessionBots();
	}
}

function toggleDebugMode() {
	debugStore.toggleDebugMode();
}

function openAlternisTab() {
	window.open(`https://alternis.notitia.world/s/${props.sessionId}`, "_blank");
}

async function createSessionWithRtp() {
	if (!instanceWorkspacesStore.openedWorkspaceId) return;
	if (instanceWorkspacesStore.isOpenedWorkspaceLocked) return;
	isCreating.value = true;
	alternisNewStore.createSessionWithRtp(
		domainStore.domain.id,
		instanceWorkspacesStore.openedWorkspaceId,
		instanceWorkspacesStore.openedWorkspaceId + " (Notitia.Operator)",
	);
	isCreating.value = false;
}

onMounted(async () => {
	loadBots();
	loadSessionBots();
	isCreating.value = false;
	if (instanceWorkspacesStore.openedWorkspaceId) {
		await dataspaceStore.loadEnvironmentVariables(domainStore.domain.id, dataspaceStore.openedDataspaceId || "");
		if (dataspaceStore.environmentVariables["AUTO_CREATE_RTP_SESSION"] === "true") {
			await createSessionWithRtp();
		}
	}
});
</script>

<style lang="scss" scoped>
@import "../../../../styles/responsiveLayout.scss";

.user-chips {
	margin: 0 0.15rem;
}

.top-bar {
	box-shadow: 0 0 5px 0 rgba(0, 0, 0, 0.5);
	height: 48px;
	min-height: 48px;
}
@media (max-width: $rl-breakpoint) {
	.top-bar {
		padding-left: 10px;
		padding-right: 10px;
	}
}
</style>
